/**
 * B40 — WHERE A BOARD-WIDE BEAT STARTS, AND HOW FAST IT TRAVELS.
 *
 * Three of the board's new motions are the same shape: every tile does the same
 * thing, but not at the same moment — the arrival cascade runs out of the middle
 * of the board, the live dim runs out of the middle, and the settled dim runs out
 * of the WINNER. All that differs is the origin, so all three are one function
 * over points and an origin, and the layouts spend it as a custom property.
 *
 * It is a pure module rather than two copies of the arithmetic inside
 * `<ArcOverlay>` and `<StackBoard>` for the usual reason: the desktop board
 * ranks REAL tile boxes (`TilePlacement.centreX/centreY`) and the phone ranks
 * grid positions, and a wave whose order differs between the two layouts is
 * exactly the kind of thing nobody notices until the owner sees it on a phone.
 *
 * TIES SHARE A RANK. That is the whole reason the delay is a rank and not the
 * distance: a board is symmetric, and two tiles the same distance from the
 * middle have to move together or the cascade reads as a diagonal wipe rather
 * than as the board opening outwards.
 */

export interface BoardPoint {
  x: number
  y: number
}

/** The arrival cascade's step, ms (owner: "roughly 25ms apart"). */
export const TILE_ENTER_STEP_MS = 25
/** The dim wave's step, ms. Slower than the arrival — a dim is a recede, not an entrance. */
export const TILE_WAVE_STEP_MS = 30

/**
 * The longest delay any tile may be given, ms.
 *
 * A board is eleven tiles today and could be more; without a ceiling the last
 * tile of a bigger board would still be arriving after the round has moved on.
 * The clamp is on the DELAY, not on the rank, so the order never changes — the
 * tail simply lands together.
 */
export const TILE_CASCADE_MAX_MS = 320

/**
 * Distances are compared after rounding to this many px, so two tiles that are
 * the same distance from the middle to within a rounding error are one rank.
 * The desktop geometry emits fractional px (the board is laid out in reference
 * px and scaled by one transform), so an exact compare would split symmetric
 * pairs on the seventeenth decimal.
 */
const DISTANCE_EPSILON_PX = 0.5

function distanceBetween(a: BoardPoint, b: BoardPoint): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.sqrt(dx * dx + dy * dy)
}

/** The centre of the bounding box the points occupy — the board's middle, not its mean. */
export function centreOf(points: readonly BoardPoint[]): BoardPoint {
  const first = points[0]
  if (first === undefined) return { x: 0, y: 0 }
  let minX = first.x
  let maxX = first.x
  let minY = first.y
  let maxY = first.y
  for (const point of points) {
    if (point.x < minX) minX = point.x
    if (point.x > maxX) maxX = point.x
    if (point.y < minY) minY = point.y
    if (point.y > maxY) maxY = point.y
  }
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 }
}

/**
 * One point per item in a single row — what the phone's grids rank on. The unit
 * is the grid position, not a pixel: the stack's tiles are laid out by CSS and
 * this module never measures anything.
 */
export function rowPoints(count: number): BoardPoint[] {
  const out: BoardPoint[] = []
  for (let i = 0; i < count; i++) out.push({ x: i, y: 0 })
  return out
}

/**
 * The delay each point takes, ms, ranked by distance from `origin`.
 *
 * Returns one entry per input point, in the input's own order — the caller
 * hands it tiles in render order and reads the delays back the same way.
 */
export function cascadeDelaysMs(
  points: readonly BoardPoint[],
  origin: BoardPoint,
  stepMs: number,
  maxMs: number = TILE_CASCADE_MAX_MS
): number[] {
  if (points.length === 0) return []
  const distances: number[] = []
  for (const point of points) distances.push(distanceBetween(point, origin))

  // The distinct distances, nearest first. `sort` on a copy — the caller's array
  // is theirs, and `distances` is read again below by index.
  const ordered = distances.slice().sort((a, b) => a - b)
  const bands: number[] = []
  for (const distance of ordered) {
    const last = bands.length === 0 ? undefined : bands[bands.length - 1]
    if (last === undefined || distance - last > DISTANCE_EPSILON_PX) bands.push(distance)
  }

  const out: number[] = []
  for (const distance of distances) {
    let rank = 0
    for (let band = 0; band < bands.length; band++) {
      const edge = bands[band]
      if (edge !== undefined && distance - edge > DISTANCE_EPSILON_PX) rank = band + 1
    }
    out.push(Math.min(rank * stepMs, maxMs))
  }
  return out
}
